import {
  ActionType,
  DateTimeDisplay,
  getResourceLifecycleStatus,
  Listing,
  ListingColumn,
  ListingComponent,
  optimizedRetryOption,
  SortDirections,
  stateT,
  Status,
} from "o4a-react";
import * as React from "react";
import { SelectionMode } from "@fluentui/react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import { DetailsPanelId, PageId } from "../../constants/pluginConstants";
import { ListingTestIds, MonochromeIconIds, ttlOneMinCaching } from "../../constants/uiConstants";
import {
  BackupSummary,
  BackupSummaryCollection,
  BackupSummaryTypeEnum,
} from "../../gen/clients/mchub-azure-api-client-exa";
import { parseId } from "../../helpers/idHelper";
import {
  getBackupTypeStr,
  getStatusInfo,
  ResourceStatus,
  responseItemstoArray,
  statusSortComparator,
} from "../../helpers/resourceHelper";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useOperation } from "../../hooks/useOperation";
import { useQueryCall } from "../../hooks/useQueryCall";
import { useRowCount } from "../../hooks/useRowCount";
import { useSidePanel } from "../../hooks/useSidePanel";
import { ExaDbCreateBackupProps, newExaDbCreateBackup } from "../../operations/ExaDb/ExaDbCreateBackup";
import { ExaDbDeleteBackupProps, newExaDbDeleteBackup } from "../../operations/ExaDb/ExaDbDeleteBackup";
import { ExaDbRestoreFromBackupProps, newExaDbRestoreFromBackup } from "../../operations/ExaDb/ExaDbRestoreFromBackup";
import { chainFunctions, getOciRegion } from "../../utils";

export interface ExaDbBackupsListProps {
  databaseName: string;
  location: string;
  databaseId: string;
}

export enum ActionIds {
  CreateBackup = "create-backup",
  // CreateDbFromBackup = "create-db-from-backup",
  Refresh = "refresh",
  Restore = "restore",
  Delete = "delete",
}

export enum ActionBarTestIds {
  CreateBackup = "create-backup",
  Delete = "delete",
  Restore = "restore",
  Refresh = "refresh",
}

export enum ColumnIds {
  Name = "displayName",
  ResStatus = "status",
  Type = "type",
  Started = "timeStarted",
  Ended = "timeEnded",
}

export enum ColumnTestIds {
  Name = "displayName",
  ResStatus = "status",
  Type = "type",
  Started = "timeStarted",
  Ended = "timeEnded",
}

export enum BackupType {
  Incremental = "INCREMENTAL"
}

export const ExaDbBackupsList = (
  { location, databaseName, databaseId }: ExaDbBackupsListProps,
): JSX.Element => {
  const { subscriptionId, resourceGroup } = parseId(databaseId);
  const resGroup = decodeURIComponent(resourceGroup || "");

  const { locale } = React.useContext<Settings>(SettingsContext);

  const { trackActionClick, trackActionDiscard } = useAnalytics();
  const pageId = PageId.EXADB_CDB_DETAILS;

  const [selection, setSelection] = React.useState<BackupSummary[]>([]);

  const { subListingPageItemCount } = useRowCount();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setSelectedItems = (selectedItems: any[]): void => {
    setSelection(selectedItems);
  };

  const [listingComponentRef, setListingComponentRef] = React.useState<ListingComponent>();

  const {
    response: listBackupsResponse, loading: listBackupsLoading,
    refresh: listBackupsRefresh,
  } = useQueryCall({
    wait: !(subscriptionId && resGroup && location && databaseId),
    method: apiClients.withRegion(getOciRegion(location)).exaDatabaseApi.listDatabaseBackups,
    options: {
      args: {
        subscriptionId,
        resourceGroupName: resGroup,
        databaseName,
      },
      caching: ttlOneMinCaching,
      fetchAllPages: true,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadBackups(),
      },
    },
  });

  const backupItems = listBackupsResponse
    && responseItemstoArray<BackupSummaryCollection, BackupSummary>(listBackupsResponse);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = React.useMemo(() => backupItems, [JSON.stringify(backupItems)]);

  const getDateTime = (value: Date | undefined): JSX.Element => <DateTimeDisplay date={value} />;
  const getBackupType = (value: BackupSummary): JSX.Element => (
    <div>{getBackupTypeStr(value?.type as BackupSummaryTypeEnum)}</div>
  );

  const getStatus = (value: BackupSummary): JSX.Element => (
    <Status
      label={stateT(value?.status || "")}
      tooltip={stateT(value?.status || "")}
      status={getResourceLifecycleStatus(value?.status || "")}
      statusInfo={
        getStatusInfo(
          value?.status || "",
          value?.lastOperationStatus || "",
          value?.lastOperationStatusDetails || "",
          value?.lifecycleDetails,
        )
      }
    />
  );

  const columns: ListingColumn[] = [
    {
      itemProp: ColumnIds.Name,
      testId: ColumnTestIds.Name,
      name: Messages.common.name(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
    },
    {
      itemProp: ColumnIds.ResStatus,
      testId: ColumnTestIds.ResStatus,
      name: Messages.labels.status(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      comparator: (
        a: BackupSummary,
        b: BackupSummary,
      ) => statusSortComparator<BackupSummary>(a, b, locale),
      isResizable: true,
      onRenderItems: value => getStatus(value),
    },
    {
      itemProp: ColumnIds.Type,
      testId: ColumnTestIds.Type,
      name: Messages.labels.type(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      onRenderItems: value => getBackupType(value),
    },
    {
      itemProp: ColumnIds.Started,
      testId: ColumnTestIds.Started,
      name: Messages.labels.started(),
      flexGrow: 1,
      initialSortDirection: SortDirections.DESC,
      isResizable: true,
      onRenderItems: value => getDateTime(value.timeStarted),
    },
    {
      itemProp: ColumnIds.Ended,
      testId: ColumnTestIds.Ended,
      name: Messages.labels.ended(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      onRenderItems: value => getDateTime(value.timeEnded),
    },
  ];

  // eslint-disable-next-line max-len
  const backupsNotAllowedToDelete = !selection.length || selection[0].type === BackupType.Incremental || !(selection[0]?.status === ResourceStatus.Failed || selection[0]?.status === ResourceStatus.Succeeded || selection[0]?.status === ResourceStatus.Active);

  const { trigger: triggerCreateBackup } = useOperation<ExaDbCreateBackupProps>(newExaDbCreateBackup);
  const { trigger: triggerRestoreFromBackup } = useOperation<ExaDbRestoreFromBackupProps>(newExaDbRestoreFromBackup);
  const { trigger: triggerDeleteBackup } = useOperation<ExaDbDeleteBackupProps>(newExaDbDeleteBackup);

  const { closePanels } = useSidePanel();

  const backupActions: ActionType[] = [
    {
      key: ActionIds.CreateBackup,
      testId: ActionBarTestIds.CreateBackup,
      text: Messages.actions.create(),
      icon: MonochromeIconIds.Create,
      onClick: () => {
        listingComponentRef?.resetTableSelection();
        trackActionClick(ActionIds.CreateBackup, pageId, DetailsPanelId.BACKUPS);
        triggerCreateBackup({
          databaseId,
          targetId: ActionIds.CreateBackup,
          location,
          onExecute: listBackupsRefresh,
          onCancel: () => trackActionDiscard(ActionIds.CreateBackup, pageId, DetailsPanelId.BACKUPS),
        });
      },
    },
    {
      key: ActionIds.Restore,
      testId: ActionBarTestIds.Restore,
      text: Messages.actions.restore(),
      icon: MonochromeIconIds.Database,
      // eslint-disable-next-line max-len
      disabled: !selection.length || !(selection[0]?.status === ResourceStatus.Succeeded || selection[0]?.status === ResourceStatus.Active),
      title: Messages.labels.restore(),
      description: Messages.hints.restoreBackupConfirmation(selection[0] ? selection[0].displayName : ""),
      onClick: () => {
        trackActionClick(ActionIds.Restore, pageId, DetailsPanelId.BACKUPS);
      },
      onConfirm: () => {
        triggerRestoreFromBackup({
          backup: {
            id: selection[0].id,
            timeEnded: selection[0].timeEnded,
          },
          databaseId,
          location,
          onExecute: () => {
            listingComponentRef?.resetTableSelection();
            listBackupsRefresh();
          },
        });
      },
      onCancel: () => trackActionDiscard(ActionIds.Restore, pageId, DetailsPanelId.BACKUPS),
    },
    //  Create database from backup has been pushed to post GA
    // {
    //   key: ActionIds.CreateDbFromBackup,
    //   text: Messages.actions.createDatabase(),
    //   icon: MonochromeIconIds.Database,
    //   disabled: !selection.length,
    //   onClick: () => {
    //     trackActionClick(ActionIds.CreateDbFromBackup, pageId, DetailsPanelId.BACKUPS);
    //     navigateTo(EXADB_CREATE_ROUTE, PageId.EXADB_CREATE, {
    //       vmClusterId: database.vmClusterId,
    //       backup: {
    //         id: selection[0].id,
    //         name: selection[0].displayName,
    //         startDate: selection[0].timeStarted,
    //       },
    //       location,
    //       analytics: {
    //         pageId,
    //         panelId: DetailsPanelId.BACKUPS,
    //         actionName: ActionIds.CreateDbFromBackup,
    //       } as NavigationAnalyticsData,
    //     });
    //   },
    // },
    {
      key: ActionIds.Refresh,
      testId: ActionBarTestIds.Refresh,
      text: Messages.actions.refresh(),
      icon: MonochromeIconIds.Refresh,
      onClick: () => {
        trackActionClick(ActionIds.Refresh, pageId, DetailsPanelId.BACKUPS);
        chainFunctions(listBackupsRefresh, listingComponentRef?.resetTableSelection);
      },
    },
    {
      key: ActionIds.Delete,
      testId: ActionBarTestIds.Delete,
      text: Messages.actions.delete(),
      icon: MonochromeIconIds.Delete,
      disabled: backupsNotAllowedToDelete,
      title: Messages.labels.deleteBackup(),
      description: Messages.hints.deleteBackupConfirmation(selection[0] ? selection[0].displayName : ""),
      onClick: () => {
        trackActionClick(ActionIds.Delete, pageId, DetailsPanelId.BACKUPS);
      },
      onConfirm: () => {
        triggerDeleteBackup({
          backupId: selection[0].id,
          location,
          onExecute: () => {
            listingComponentRef?.resetTableSelection();
            listBackupsRefresh();
          },
        });
      },
      onCancel: () => trackActionDiscard(ActionIds.Delete, pageId, DetailsPanelId.BACKUPS),
    },
  ];

  return (
    <Listing
      testId={ListingTestIds.ExaDbBackups}
      listingComponentRef={setListingComponentRef}
      itemsPerPage={subListingPageItemCount}
      items={items || []}
      emptyList={{ title: Messages.common.noResults() }}
      listColumns={columns}
      itemKey={ColumnIds.Name}
      actionBarItems={backupActions}
      onActionClick={closePanels}
      selectionMode={SelectionMode.single}
      selectedItems={setSelectedItems}
      isLoading={listBackupsLoading}
      sorting={{
        locale,
        initialSortedColumn: ColumnIds.Started,
      }}
    />
  );
};
